// Geometry for the Sticker Studio: layer shape paths, text measuring, the cut line, the bleed box and the sheet layout.
import { SHEETS, SHEET_MARGIN, sheetOf } from "./stickerModel";

const f = (v) => Math.round(v * 10000) / 10000;

// Shape path in a box of w × h centred on the origin (SVG path data — also fed to canvas Path2D).
export const shapePath = (shape, w, h, radius = 0) => {
  const x = -w / 2, y = -h / 2;
  switch (shape) {
    case "rounded": { const r = Math.min(radius, w / 2, h / 2); return `M${f(x + r)} ${f(y)}H${f(x + w - r)}A${f(r)} ${f(r)} 0 0 1 ${f(x + w)} ${f(y + r)}V${f(y + h - r)}A${f(r)} ${f(r)} 0 0 1 ${f(x + w - r)} ${f(y + h)}H${f(x + r)}A${f(r)} ${f(r)} 0 0 1 ${f(x)} ${f(y + h - r)}V${f(y + r)}A${f(r)} ${f(r)} 0 0 1 ${f(x + r)} ${f(y)}Z`; }
    case "circle": case "oval": { const rx = w / 2, ry = h / 2; return `M${f(-rx)} 0A${f(rx)} ${f(ry)} 0 1 0 ${f(rx)} 0A${f(rx)} ${f(ry)} 0 1 0 ${f(-rx)} 0Z`; }
    case "triangle": return `M0 ${f(y)}L${f(x + w)} ${f(y + h)}H${f(x)}Z`;
    case "hexagon": return poly(6, w, h, -90);
    case "star": return starPath(5, w, h, 0.42);
    case "badge": return starPath(12, w, h, 0.82);
    case "heart": { const s = Math.min(w, h); const k = s / 32; const P = (x, y) => `${f(x * k)} ${f((y - 14) * k)}`; return `M${P(0, 12)}C${P(0, 4)} ${P(-6, 0)} ${P(-10, 0)}C${P(-14, 0)} ${P(-16, 3)} ${P(-16, 7)}C${P(-16, 16)} ${P(-4, 22)} ${P(0, 28)}C${P(4, 22)} ${P(16, 16)} ${P(16, 7)}C${P(16, 3)} ${P(14, 0)} ${P(10, 0)}C${P(6, 0)} ${P(0, 4)} ${P(0, 12)}Z`; }
    default: return `M${f(x)} ${f(y)}H${f(x + w)}V${f(y + h)}H${f(x)}Z`;
  }
};
const poly = (n, w, h, start) => "M" + Array.from({ length: n }, (_, i) => { const a = ((start + (360 / n) * i) * Math.PI) / 180; return `${f((Math.cos(a) * w) / 2)} ${f((Math.sin(a) * h) / 2)}`; }).join("L") + "Z";
const starPath = (n, w, h, inner) => "M" + Array.from({ length: n * 2 }, (_, i) => { const a = (-90 + (180 / n) * i) * (Math.PI / 180), k = i % 2 ? inner : 1; return `${f((Math.cos(a) * w * k) / 2)} ${f((Math.sin(a) * h * k) / 2)}`; }).join("L") + "Z";

// ---- text ----
let mctx;
export const fontString = (l, px) => `${l.italic ? "italic " : ""}${l.weight || 400} ${px}px "${l.font}"`;
export const textLines = (l) => String(l.text ?? "").split("\n");
// Text box in inches (w, h) measured with the real font — falls back to a rough estimate before the font has loaded.
export const textBox = (l) => {
  if (!mctx) mctx = document.createElement("canvas").getContext("2d");
  const px = 100;
  mctx.font = fontString(l, px);
  try { mctx.letterSpacing = `${(l.spacing || 0) * 100}px`; } catch { /* older browsers */ }
  const lines = textLines(l);
  const w = Math.max(...lines.map((t) => mctx.measureText(t || " ").width / px), 0.1) * l.size;
  return { w, h: lines.length * l.size * (l.lineHeight || 1.1) };
};
export const layerBox = (l) => (l.kind === "text" ? textBox(l) : { w: l.w, h: l.h });

// ---- cut line (inches, sticker box origin top-left) ----
const ring = (pts) => pts.map(([x, y]) => [f(x), f(y)]);
export const ellipseLoop = (cx, cy, rx, ry, n = 72) => ring(Array.from({ length: n }, (_, i) => { const a = (i / n) * Math.PI * 2; return [cx + Math.cos(a) * rx, cy + Math.sin(a) * ry]; }));
export const roundedLoop = (w, h, r) => {
  r = Math.min(r, w / 2, h / 2);
  if (r <= 0) return [[0, 0], [w, 0], [w, h], [0, h]];
  const out = [];
  const arc = (cx, cy, a0) => { for (let i = 0; i <= 8; i++) { const a = a0 + (i / 8) * (Math.PI / 2); out.push([cx + Math.cos(a) * r, cy + Math.sin(a) * r]); } };
  arc(w - r, r, -Math.PI / 2); arc(w - r, h - r, 0); arc(r, h - r, Math.PI / 2); arc(r, r, Math.PI);
  return ring(out);
};
export const cutLoops = (doc) => {
  const { w, h } = doc;
  switch (doc.shape) {
    case "contour": return doc.cut?.pieces?.length ? doc.cut.pieces : [roundedLoop(w, h, 0)];
    case "rounded": return [roundedLoop(w, h, doc.radius)];
    case "circle": { const d = Math.min(w, h); return [ellipseLoop(w / 2, h / 2, d / 2, d / 2)]; }
    case "oval": return [ellipseLoop(w / 2, h / 2, w / 2, h / 2)];
    default: return [roundedLoop(w, h, 0)];
  }
};
export const loopsPath = (loops) => loops.map((L) => "M" + L.map((p) => `${p[0]} ${p[1]}`).join("L") + "Z").join("");
export const loopsBox = (loops) => {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  loops.forEach((L) => L.forEach(([x, y]) => { x0 = Math.min(x0, x); y0 = Math.min(y0, y); x1 = Math.max(x1, x); y1 = Math.max(y1, y); }));
  return isFinite(x0) ? { x: x0, y: y0, w: x1 - x0, h: y1 - y0 } : { x: 0, y: 0, w: 1, h: 1 };
};
// The printed tile: cut line grown by the bleed on every side.
export const tileBox = (doc) => { const b = loopsBox(cutLoops(doc)); return { x: b.x - doc.bleed, y: b.y - doc.bleed, w: b.w + 2 * doc.bleed, h: b.h + 2 * doc.bleed }; };

// ---- sheet layout: tiles in a grid inside the margins; a roll grows in length ----
export const sheetLayout = (doc) => {
  const sheet = sheetOf(doc) || SHEETS[0];
  const tile = tileBox(doc);
  const usableW = sheet.w - 2 * SHEET_MARGIN;
  const fit = (tw, th, rot) => {
    const cols = Math.max(1, Math.floor((usableW + doc.gap) / (tw + doc.gap)));
    const rowsAll = Math.ceil(doc.copies / cols);
    const rows = sheet.h ? Math.max(1, Math.min(rowsAll, Math.floor((sheet.h - 2 * SHEET_MARGIN + doc.gap) / (th + doc.gap)))) : rowsAll;
    return { cols, rows, rot, tw, th, per: cols * rows };
  };
  const a = fit(tile.w, tile.h, 0), b = fit(tile.h, tile.w, 90);
  const g = tile.w > usableW && tile.h <= usableW ? b : b.per > a.per ? b : a;
  const onSheet = Math.min(doc.copies, g.per);
  const H = sheet.h || 2 * SHEET_MARGIN + g.rows * (g.th + doc.gap) - doc.gap;
  const gridW = g.cols * (g.tw + doc.gap) - doc.gap, gridH = g.rows * (g.th + doc.gap) - doc.gap;
  const ox = (sheet.w - gridW) / 2, oy = sheet.h ? (sheet.h - gridH) / 2 : SHEET_MARGIN;
  const cells = [];
  for (let i = 0; i < onSheet; i++) {
    const c = i % g.cols, r = Math.floor(i / g.cols);
    cells.push({ x: ox + c * (g.tw + doc.gap), y: oy + r * (g.th + doc.gap), w: g.tw, h: g.th, rot: g.rot });
  }
  return { sheet: { ...sheet, h: H }, tile, cells, per: g.per, sheets: Math.max(1, Math.ceil(doc.copies / g.per)), rot: g.rot, fits: tile.w <= usableW || tile.h <= usableW };
};
// Registration marks: ¼″ black squares in the four corners of the sheet, inset ⅜″ (generic marks every cutter can read).
export const regMarks = (sheet) => { const m = 0.375, s = 0.25; return [[m, m], [sheet.w - m - s, m], [m, sheet.h - m - s], [sheet.w - m - s, sheet.h - m - s]].map(([x, y]) => ({ x, y, s })); };
